import { Component, inject, Signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { EntityType, EventType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

interface ButtonConfig {
  flag: Signal<string>;
  id: string;
  onClick: () => Promise<void>;
}

const MessagesConstants = {
  BILL_COM_RETRIEVAL: 'Beginning Bill.com data retrieval...',
  COUPA_RETRIEVAL: 'Beginning Coupa data retrieval...',
  DYNAMICS365_FO_RETRIEVAL: 'Beginning Dynamics 365 F&O data retrieval...',
  DYNAMICS365_RETRIEVAL: 'Beginning Dynamics 365 data retrieval...',
  FLAT_FILE_RETRIEVAL: 'Beginning Flat File data retrieval...',
  NETSUITE_RETRIEVAL: 'Beginning Netsuite data retrieval...',
  ORACLE_FUSION_RETRIEVAL: 'Beginning Oracle Fusion data retrieval...',
  QUICKBOOKS_RETRIEVAL: 'Beginning Quickbooks Online data retrieval...',
  SAGE_INTACCT_RETRIEVAL: 'Beginning Sage Intacct data retrieval...',
};

// eslint-disable-next-line max-lines-per-function
const getIntegrationButtons = (): ButtonConfig[] => {
  const launchDarklyService = inject(LaunchDarklyService);
  const eventService = inject(EventService);
  const overlayService = inject(OverlayService);

  const sendEvent = async (eventType: EventType, msg: string) => {
    await firstValueFrom(
      eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          triggered_by_user: true,
        }),
        type: eventType,
      })
    );

    overlayService.success(msg);
  };

  return [
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_bill_com_integration),
      id: 'bill-com',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_BILL_COM, MessagesConstants.BILL_COM_RETRIEVAL);
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_coupa_integration),
      id: 'coupa',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_COUPA, MessagesConstants.COUPA_RETRIEVAL);
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_dynamics365_integration),
      id: 'dynamics365',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_DYNAMICS365, MessagesConstants.DYNAMICS365_RETRIEVAL);
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_dynamics365_fo_integration),
      id: 'dynamics365-fo',
      onClick: async () => {
        await sendEvent(
          EventType.REFRESH_DYNAMICS365_FO,
          MessagesConstants.DYNAMICS365_FO_RETRIEVAL
        );
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_flat_file_integration),
      id: 'flat-file',
      onClick: async () => {
        await sendEvent(
          EventType.REFRESH_FLAT_FILE_INTEGRATION,
          MessagesConstants.FLAT_FILE_RETRIEVAL
        );
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_netsuite_integration),
      id: 'netsuite',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_NETSUITE, MessagesConstants.NETSUITE_RETRIEVAL);
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_oracle_fusion_integration),
      id: 'oracle-fusion',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_ORACLE_FUSION, MessagesConstants.ORACLE_FUSION_RETRIEVAL);
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_quickbooks_online_integration),
      id: 'quickbooks-online',
      onClick: async () => {
        await sendEvent(
          EventType.REFRESH_QUICKBOOKS_ONLINE,
          MessagesConstants.QUICKBOOKS_RETRIEVAL
        );
      },
    },
    {
      flag: launchDarklyService.$select((flags) => flags.cronjob_sage_intacct_integration),
      id: 'sage-intacct',
      onClick: async () => {
        await sendEvent(EventType.REFRESH_SAGE_INTACCT, MessagesConstants.SAGE_INTACCT_RETRIEVAL);
      },
    },
  ];
};

@Component({
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [ButtonComponent, TooltipDirective],
  selector: 'aux-invoices-sync-buttons',
  styles: [
    `
      :host {
        @apply flex flex-wrap gap-1.5;
      }
    `,
  ],
  template: `
    @for (button of buttons; track button.id) {
      @if (button.flag()) {
        <aux-button
          [attr.data-id]="button.id"
          icon="CloudDownload"
          label="Sync Invoices"
          variant="secondary"
          [auxTooltip]="invoiceCtx.invoiceLockTooltip()"
          [disabled]="invoiceCtx.disabledSyncInvoicesUI()"
          [onClick]="button.onClick.bind(this)"
        />
      }
    }
  `,
})
export class InvoicesSyncButtonsComponent {
  eventQuery = inject(EventQuery);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  workflowQuery = inject(WorkflowQuery);

  buttons: ButtonConfig[] = getIntegrationButtons();
}
